<template>
	<div>
		<div class="list-page">
			<div class="title-container">
				<div class="title"><span>{{ numFiles }}</span> Product Photo{{ numFiles !== 1 ? 's' : '' }}</div>
			</div>
			<div class="gallery-upload-container remove-margin-bottom">
				<div class="gallery-upload-box" :class="{ dragging }">
					<form ref="fileform" @drop="onDrop" enctype="multipart/form-data">
						<div class="gallery-upload-icon"><span class="fa fa-cloud-upload"></span></div>
						<div class="gallery-upload-instructions">Click or drag your product photos here!</div>
						<div class="gallery-upload-disclaimer">* Only JPG, JPEG, PNG and GIF files may be uploaded</div>
						<div class="gallery-upload-disclaimer">* Maximum allowable file size is 10 megabytes</div>
						<input type="file" multiple @change="onSelect">
					</form>
				</div>
			</div>
		</div>
		<div class="buffer small"></div>
		<div class="grid two">
			{{ /* Multi Variant */ }}
			<div v-if="hasVariants" v-sortable="{ onUpdate: reorderPhotos, handle: '.input-dragger', scroll: true }" class="conjoin-input-containers">
				<div v-for="file in activeFiles" :key="`file-${ file.id }`">
					<div class="input-container">
						<div class="input-title">Select Variant</div>
						<div class="input-box">
							<variant-selectize :form="form" key-prefix="photo-variant" v-model="file.variant_id" none="All"></variant-selectize>
						</div>
						<div class="inline-buttons for-input-title">
							<a href @click.prevent="removeFile(file.id)"><div class="hover-title">Delete</div><span class="fa fa-trash"></span></a>
							<div class="input-dragger"><span class="fa fa-sort"></span></div>
						</div>
					</div>
					<div class="input-container">
						<a :href="file.largeSrc || ''" @click="(e) => onClickFile(e, file)" class="input-photo vertical-align-top" :class="{ 'not-clickable': file.isNew }" target="_blank"><img :src="file.src"></a>
						<div class="input-box vertical-align-top"><textarea-autosize v-model="file.caption" placeholder="Please type your caption here"></textarea-autosize></div>
					</div>
				</div>
			</div>

			{{ /* Single Variant */ }}
			<div v-if="!hasVariants" v-sortable="{ onUpdate: reorderPhotos, handle: '.input-dragger', scroll: true }">
				<div v-for="file in activeFiles" :key="`file-${ file.id }`" class="input-container">
					<a :href="file.largeSrc || ''" @click="(e) => onClickFile(e, file)"  class="input-photo for-photo-gallery vertical-align-top" target="_blank"><img :src="file.src"></a>
					<div class="input-box vertical-align-top"><textarea-autosize v-model="file.caption" placeholder="Please type your caption here"></textarea-autosize></div>
					<a href @click.prevent="removeFile(file.id)" class="input-icon-button"><span class="fa fa-trash"></span></a>
					<div class="input-dragger"><span class="fa fa-sort"></span></div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>

import _ from 'underscore';
import ProductFormPartMixin from '../mixins/ProductFormPartMixin';
import VariantSelectize from './VariantSelectize';

export default {

	mixins: [ProductFormPartMixin],

	data() {
		return {
			files: [],
			form: this.value,
			dragging: false,
			src: null,
		};
	},

	computed: {

		numFiles() {
			return (this.form.files || []).length;
		},

		activeFiles() {
			return (this.form.files || []).filter(file => !file.pendingDelete);
		},

	},

	mounted() {
		/*
		  If drag and drop capable, then we continue to bind events to our elements.
		*/
		if (!this.hasDragAndDropCapabilities()) {
			this.preventDefaultDropBehavior();
		}
	},

	methods: {

		onDrop(e) {
			this.addFiles(e.dataTransfer.files);
		},

		onSelect(e) {
			this.addFiles(e.target.files);

			// Wipe out the input form
			this.$refs.fileform.reset();
		},

		/**
		 * Attach and upload files
		 */
		addFiles(files) {
			if (!this.form.files) this.$set(this.form, 'files', []);

			for (let i = 0; i < files.length; i++) {
				const transfer = files[ i ];
				this.form.files.unshift({
					id: _.uniqueId('new-file'),
					transfer,
					src: '/images/not_available-generic.png',
					variant_id: 0,
					progress: 0,
					result: null,
					started: false,
					isNew: true,
				});
			}

			this.getImagePreviews();
		},

		onClickFile(event, file) {
			if (file.isNew) {
				event.preventDefault();
			}
		},

		/*
		  Determines if the drag and drop functionality is in the
		  window
		*/
		hasDragAndDropCapabilities() {
			/*
			  Create a test element to see if certain events
			  are present that let us do drag and drop.
			*/
			var div = document.createElement('div');

			/*
			  Check to see if the `draggable` event is in the element
			  or the `ondragstart` and `ondrop` events are in the element. If
			  they are, then we have what we need for dragging and dropping files.

			  We also check to see if the window has `FormData` and `FileReader` objects
			  present so we can do our AJAX uploading
			*/
			return (('draggable' in div)
				|| ('ondragstart' in div && 'ondrop' in div))
				&& 'FormData' in window
				&& 'FileReader' in window;
		},

		/*
		  Gets the image preview for the file.
		*/
		getImagePreviews() {
			/*
			  Iterate over all of the files and generate an image preview for each one.
			*/
			this.form.files.forEach((file, key) => {
				if (!file.isNew) return;
				// Ensure the file is an image file
				if (file.transfer.type.match('image.*')) {
					let reader = new FileReader();

					/*
					  Add an event listener for when the file has been loaded
					  to update the src on the file preview.
					*/
					reader.addEventListener('load', () => {
						this.$set(this.form.files[ key ], 'src', reader.result);
					});

					/*
					  Read the data for the file in through the reader. When it has
					  been loaded, we listen to the event propagated and set the image
					  src to what was loaded from the reader.
					*/
					reader.readAsDataURL(file.transfer);
				}
			});
		},

		/*
		 * Removes a select file the user has uploaded
		*/
		removeFile(id) {
			const index = _.findIndex(this.form.files, { id });
			if (index !== -1) {
				if (this.form.files[index].isNew) {
					this.$delete(this.form.files, index);
				} else {
					this.$set(this.form.files[index], 'pendingDelete', true);
				}
			}
		},

		reorderPhotos(event) {
			this.form.files.splice(event.newIndex, 0, this.form.files.splice(event.oldIndex, 1)[ 0 ]);
		},

		/*
		 * For each event add an event listener that prevents the default action
		 * (opening the file in the browser) and stop the propagation of the event (so
		 * no other elements open the file in the browser)
		*/
		preventDefaultDropBehavior() {
			/*
			  Listen to all of the drag events and bind an event listener to each
			  for the fileform.
			*/
			['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach(eventName => {

				this.$refs.fileform.addEventListener(eventName, e => {
					this.dragging = ['drag', 'dragstart', 'dragover', 'dragenter'].indexOf(eventName) !== -1;

					e.preventDefault();
					e.stopPropagation();
				});
			});
		},

	},

	components: {
		VariantSelectize,
	},

};

</script>
